import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cx } from '@/lib/cx';
import styles from './CtaButton.module.css';

export type CtaVariant = 'primary' | 'gold' | 'outline' | 'ghost';
export type CtaSize = 'sm' | 'md' | 'lg';

interface CtaBase {
  children: ReactNode;
  variant?: CtaVariant;
  size?: CtaSize;
  /** Ornamento a la izquierda del texto (destello, corona VIP...). */
  icon?: ReactNode;
  /** Ocupa todo el ancho disponible (movil). */
  block?: boolean;
  className?: string;
}

type CtaProps =
  | (CtaBase & { as: 'internal'; to: string; onClick?: never; type?: never })
  | (CtaBase & { as: 'external'; href: string; onClick?: never; type?: never })
  | (CtaBase & { as?: 'button'; to?: never; href?: never; onClick?: () => void; type?: 'button' | 'submit' });

/**
 * CTA del sistema: capsula premium, area tactil >= 44px, foco visible.
 *
 * NO EXISTE una variante "enlace sin destino". Si un destino no tiene ficha de
 * venta, la pagina NO renderiza un CtaButton: renderiza un `TicketState`. Eso
 * hace fisicamente imposible el `href="#"` que prohibe el ADR-003 §3.
 */
export function CtaButton(props: CtaProps) {
  const { children, variant = 'primary', size = 'md', icon, block = false, className } = props;
  const classes = cx(styles.cta, styles[variant], styles[`size-${size}`], block && styles.block, className);

  const content = (
    <>
      {icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className={styles.label}>{children}</span>
    </>
  );

  if (props.as === 'internal') {
    return (
      <Link className={classes} to={props.to}>
        {content}
      </Link>
    );
  }

  if (props.as === 'external') {
    return (
      <a className={classes} href={props.href} target="_blank" rel="noopener noreferrer">
        {content}
        <span className="az-visually-hidden"> (se abre en una pestaña nueva)</span>
      </a>
    );
  }

  return (
    <button className={classes} type={props.type ?? 'button'} onClick={props.onClick}>
      {content}
    </button>
  );
}
